<script lang="ts">import { Button } from "@infinitro/button";
import { cfgBrand, cfgBrandTitle, cfgCdnBase, cfgToThemeCdnPath, cfgToThemeContentPath } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { locale, toSiteUrl } from "@infinitro/global";
import MarkdownIt from "markdown-it";
const CDN_BASE = cfgCdnBase();
const BRAND_NAME = cfgBrandTitle();
const md = new MarkdownIt({
	html: true,
	linkify: true,
	breaks: true
});
const NOT_FOUND_THEME_IMAGE = cfgToThemeCdnPath("/misc/not-found.png");
const NOT_FOUND_FALLBACK_IMAGE = `${CDN_BASE}/not-found.png`;
const CONTENT_BASE_PATH = "/not-found/not-found";
let contentPaths = [];
let htmlContent = "";
let imageSrc = NOT_FOUND_THEME_IMAGE;
let imageFallbackApplied = false;
let primaryButton = {
	text: $t(Verbiage.homepage),
	url: "/"
};
let secondaryButton = {
	text: $t(Verbiage.discover_games),
	url: "/casino/"
};
function fallbackMarkdown() {
	return `## 404 ${$t(Verbiage.page_not_found)}

${$t(Verbiage.great_to_have_you_back).replace("[BRAND]", BRAND_NAME)}

[${$t(Verbiage.homepage)}](/)
[${$t(Verbiage.discover_games)}](/casino/)`;
}
function extractButtons(markdown) {
	const matches = Array.from(markdown.matchAll(/^\s*\[([^\]]+)\]\(([^)]+)\)\s*$/gm)).map((match) => ({
		text: match[1]?.trim() || "",
		url: match[2]?.trim() || ""
	})).filter((item) => item.text && item.url).slice(0, 2);
	primaryButton = matches[0] || {
		text: $t(Verbiage.homepage),
		url: "/"
	};
	secondaryButton = matches[1] || null;
	return markdown.replace(/^\s*\[([^\]]+)\]\(([^)]+)\)\s*$/gm, "").trim();
}
function renderMarkdown(markdown) {
	const normalized = markdown.replace(/%(domain|brand)%/g, BRAND_NAME);
	const markdownWithoutButtons = extractButtons(normalized);
	htmlContent = md.render(markdownWithoutButtons);
}
async function fetchContent() {
	for (const path of contentPaths) {
		try {
			const response = await fetch(path);
			if (!response.ok) continue;
			const markdown = await response.text();
			renderMarkdown(markdown);
			return;
		} catch (error) {
			console.warn("Failed to fetch not-found markdown:", error);
		}
	}
	renderMarkdown(fallbackMarkdown());
}
function handleImageError() {
	// Single fallback hop to prevent infinite error loops.
	if (imageFallbackApplied) return;
	imageFallbackApplied = true;
	imageSrc = NOT_FOUND_FALLBACK_IMAGE;
}
$: {
	contentPaths = [cfgToThemeContentPath(`${CONTENT_BASE_PATH}.${$locale}.md`), cfgToThemeContentPath(`${CONTENT_BASE_PATH}.en.md`)];
	fetchContent();
}
</script>

<div class="not-found-container">
	<div class="not-found">
		<img
			src={`${CDN_BASE}/images/misc/not-found.png`}
			alt={$t(Verbiage.page_not_found)}
			on:error={handleImageError}
		/>
		<div class="not-found-markdown">{@html htmlContent}</div>
		<div class="not-found-buttons">
			<a href={toSiteUrl(primaryButton.url)}>
				<Button text={primaryButton.text} minWidth />
			</a>
			{#if secondaryButton}
				<a href={toSiteUrl(secondaryButton.url)}>
					<Button text={secondaryButton.text} variant="secondary" minWidth />
				</a>
			{/if}
		</div>
	</div>
</div>

<style>
	.not-found-container {
    display: flex;
    height: 100%;
    min-height: 95vh;
    width: 100%;
    align-items: center;
    justify-content: center;
    padding: 1rem
}
	.not-found {
    margin-top: -50px;
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem
}
	@media (min-width: 1024px) {
    .not-found {
        width: 50%
    }
}
	.not-found img {
    height: auto;
    width: auto;
    max-width: 300px
}

	:global(.not-found-markdown) {
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center
}
	:global(.not-found-markdown h6) {
    margin: 0px;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    --tw-text-opacity: 1;
    color: rgb(127 138 170 / var(--tw-text-opacity, 1));
    font-family: var(--font-main)
}
	:global(.not-found-markdown h2) {
    margin: 0px;
    text-align: center;
    font-size: 30px;
    font-weight: 700;
    color: var(--color-text-100);
    font-family: var(--font-main)
}
	:global(.not-found-markdown p) {
    margin-top: 0.5rem;
    text-align: center;
    font-size: 14px;
    color: var(--color-text-300);
    opacity: 0.7;
    font-family: var(--font-main)
}
	:global(.not-found-markdown h2) {
    margin-top: 0.75rem;
    font-size: 24px;
    line-height: 1.25
}
	.not-found-buttons {
    margin-top: 1rem;
    display: flex;
    justify-content: center;
    gap: 1rem
}
</style>
